/**
 * MediaGrid Component
 *
 * Grid layout for displaying images and videos from message history
 */

import React, { useState } from 'react';
import type { SearchResult } from '../../services/search.service';

interface MediaGridProps {
  messages: SearchResult[];
  scope: { channelId?: string; dmThreadId?: string };
}

export const MediaGrid: React.FC<MediaGridProps> = ({ messages }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Extract all media attachments from messages
  const mediaItems = messages.flatMap((message) =>
    (message.message_attachments || [])
      .filter((att) => att.mime_type?.startsWith('image/') || att.mime_type?.startsWith('video/'))
      .map((attachment) => ({
        ...attachment,
        message,
      }))
  );

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const navigateLightbox = (direction: 'next' | 'prev') => {
    if (lightboxIndex === null) return;

    if (direction === 'next' && lightboxIndex < mediaItems.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    } else if (direction === 'prev' && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('bg-BG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (mediaItems.length === 0) {
    return null;
  }

  return (
    <>
      <div className="media-grid">
        {mediaItems.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className="media-item"
            onClick={() => openLightbox(index)}
          >
            {item.mime_type?.startsWith('image/') ? (
              <img
                src={item.file_url || `/api/attachments/${item.id}`}
                alt={item.file_name}
                className="media-thumbnail"
              />
            ) : (
              <div className="video-thumbnail">
                <video
                  src={item.file_url || `/api/attachments/${item.id}`}
                  className="media-thumbnail"
                  muted
                />
                <div className="video-overlay">
                  <svg className="play-icon" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            )}
            <div className="media-info">
              <p className="media-sender">
                {item.message.users.display_name ||
                  `${item.message.users.first_name} ${item.message.users.last_name}`}
              </p>
              <p className="media-date">{formatDate(item.message.created_at)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
            ✕
          </button>

          {lightboxIndex > 0 && (
            <button
              className="lightbox-nav lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                navigateLightbox('prev');
              }}
              aria-label="Previous"
            >
              ‹
            </button>
          )}

          {lightboxIndex < mediaItems.length - 1 && (
            <button
              className="lightbox-nav lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                navigateLightbox('next');
              }}
              aria-label="Next"
            >
              ›
            </button>
          )}

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {mediaItems[lightboxIndex].mime_type?.startsWith('image/') ? (
              <img
                src={
                  mediaItems[lightboxIndex].file_url ||
                  `/api/attachments/${mediaItems[lightboxIndex].id}`
                }
                alt={mediaItems[lightboxIndex].file_name}
                className="lightbox-image"
              />
            ) : (
              <video
                src={
                  mediaItems[lightboxIndex].file_url ||
                  `/api/attachments/${mediaItems[lightboxIndex].id}`
                }
                controls
                autoPlay
                className="lightbox-video"
              />
            )}

            <div className="lightbox-info">
              <p className="lightbox-filename">{mediaItems[lightboxIndex].file_name}</p>
              <p className="lightbox-meta">
                Shared by{' '}
                {mediaItems[lightboxIndex].message.users.display_name ||
                  `${mediaItems[lightboxIndex].message.users.first_name} ${mediaItems[lightboxIndex].message.users.last_name}`}{' '}
                · {formatDate(mediaItems[lightboxIndex].message.created_at)}
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .media-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }

        .media-item {
          position: relative;
          cursor: pointer;
          border-radius: 0.5rem;
          overflow: hidden;
          background: #f3f4f6;
          transition: transform 0.2s;
        }

        .media-item:hover {
          transform: scale(1.02);
        }

        .media-thumbnail {
          width: 100%;
          height: 200px;
          object-fit: cover;
          display: block;
        }

        .video-thumbnail {
          position: relative;
        }

        .video-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0, 0, 0, 0.5);
          border-radius: 50%;
          padding: 1rem;
        }

        .play-icon {
          width: 2rem;
          height: 2rem;
        }

        .media-info {
          padding: 0.75rem;
        }

        .media-sender {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 500;
          color: #1f2937;
        }

        .media-date {
          margin: 0.25rem 0 0;
          font-size: 0.75rem;
          color: #6b7280;
        }

        /* Lightbox */
        .lightbox {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .lightbox-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          font-size: 2rem;
          width: 3rem;
          height: 3rem;
          border-radius: 50%;
          cursor: pointer;
          z-index: 10001;
        }

        .lightbox-close:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .lightbox-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          font-size: 3rem;
          width: 4rem;
          height: 4rem;
          border-radius: 50%;
          cursor: pointer;
          z-index: 10001;
        }

        .lightbox-nav:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .lightbox-prev {
          left: 2rem;
        }

        .lightbox-next {
          right: 2rem;
        }

        .lightbox-content {
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .lightbox-image,
        .lightbox-video {
          max-width: 100%;
          max-height: calc(90vh - 100px);
          object-fit: contain;
        }

        .lightbox-info {
          margin-top: 1rem;
          text-align: center;
          color: white;
        }

        .lightbox-filename {
          margin: 0;
          font-size: 1rem;
          font-weight: 500;
        }

        .lightbox-meta {
          margin: 0.5rem 0 0;
          font-size: 0.875rem;
          opacity: 0.8;
        }

        @media (max-width: 768px) {
          .media-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
            gap: 0.75rem;
          }

          .media-thumbnail {
            height: 150px;
          }

          .lightbox-nav {
            width: 3rem;
            height: 3rem;
            font-size: 2rem;
          }

          .lightbox-prev {
            left: 1rem;
          }

          .lightbox-next {
            right: 1rem;
          }
        }
      `}</style>
    </>
  );
};
